import type {
  GridAlignment,
  GridColDef,
  GridColTypeDef,
  GridColumnGroupingModel,
  GridColumnHeaderParams,
} from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { getDimensionTypeProps } from '@/features/DataGrid';
import { selectCurrency } from '@/entities/session';
import type { RowHeights } from '@/shared/constants';
import { DEFAULT_COLUMN_WIDTH } from '@/shared/constants';
import { useNumberFormatter } from '@/shared/hooks';
import { useAppSelector } from '@/shared/model';
import { toFormatType } from '@/shared/types';
import type {
  ColumnsConfig,
  MetricColumnDataType,
  MetricDataFormatType,
  DimensionColumnDataType,
} from '@/shared/types';
import type { TotalData } from '@/shared/ui';
import { DIMENSION_MIN_WIDTH } from '@/shared/ui';
import { Cell } from '../Cell/Cell';
import { DateTableHeader } from '../DateTableHeader/DateTableHeader';
import { DimensionTableHeader } from '../DimensionTableHeader/DimensionTableHeader';
import { DATE_COLUMN_MIN_WIDTH } from './constants';

interface DataTableProps {
  columns: GridColDef[];
  columnGroupingModel: GridColumnGroupingModel;
}

interface Params {
  total?: TotalData;
  rowHeight: RowHeights;
  columnsConfig: ColumnsConfig;
  columnsWidth: Record<string, number | undefined>;
  dimensions: string[];
  metric: string;
  dataColumns: string[];
}

export const useTableProps = ({
  total,
  rowHeight,
  columnsConfig,
  columnsWidth,
  dimensions,
  metric,
  dataColumns,
}: Params): DataTableProps => {
  const userCurrency = useAppSelector(selectCurrency);
  const formattersMap = useNumberFormatter(userCurrency);
  const columns: GridColDef[] = useMemo(
    () => {
      const metricConfig = columnsConfig[metric];
      const columnsDefs: GridColDef[] = [
        ...dimensions.map((dimension, ind) => {
          const { name, description, type } = columnsConfig[dimension];

          return {
            ...getDimensionTypeProps(type as DimensionColumnDataType, ind),
            headerName: name,
            description: description,
            field: dimension,
            minWidth: DIMENSION_MIN_WIDTH,
            width: columnsWidth[dimension] || DEFAULT_COLUMN_WIDTH,
            headerClassName: 'dimensionHeader',
            renderHeader: ({ colDef }: GridColumnHeaderParams) => {
              if (!colDef) {
                return null;
              }

              return (
                <DimensionTableHeader
                  field={colDef.field}
                  name={colDef.headerName ?? colDef.field}
                  description={colDef.description}
                />
              );
            },
          };
        }),
      ];

      if (!metricConfig) {
        return columnsDefs;
      }

      const metricFormatter =
        formattersMap[metricConfig.type as MetricColumnDataType][
          toFormatType(metricConfig.dataType as MetricDataFormatType)
        ];

      return columnsDefs.concat([
        ...dataColumns.map((column) => {
          return {
            ...getMetricTypeProps({
              rowHeight,
              total,
              metric,
              formatter: metricFormatter,
            }),
            field: column,
            minWidth: DATE_COLUMN_MIN_WIDTH,
            width: columnsWidth[column] || DEFAULT_COLUMN_WIDTH,
            disableReorder: true,
            align: 'right' as GridAlignment,
            headerClassName: 'metricHeader',
            cellClassName: 'metricCell',
            renderHeader: ({ colDef }: GridColumnHeaderParams) => {
              if (!colDef) {
                return null;
              }

              return <DateTableHeader date={colDef.field} />;
            },
          };
        }),
      ]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      total,
      dimensions,
      metric,
      dataColumns,
      columnsConfig,
      columnsWidth,
      rowHeight,
    ],
  );
  const columnGroupingModel: GridColumnGroupingModel = [
    {
      groupId: 'Dimensions',
      children: dimensions.map((dimension) => {
        return {
          field: dimension,
        };
      }),
      renderHeaderGroup: () => null,
      headerClassName: 'columnGroup',
    },
    {
      groupId: 'Dates',
      children: dataColumns.map((column) => {
        return {
          field: column,
        };
      }),
      renderHeaderGroup: () => null,
      headerClassName: 'columnGroup',
    },
  ];

  return {
    columns,
    columnGroupingModel,
  };
};

const getMetricTypeProps = ({
  rowHeight,
  total,
  metric,
  formatter,
}: {
  rowHeight: RowHeights;
  total?: TotalData;
  metric: string;
  formatter: (v: number) => string;
}): GridColTypeDef => {
  return {
    type: 'number',
    renderCell: (params) => {
      const { field, row } = params;

      if (row.isTotal) {
        return formatter(params.value);
      }

      return (
        <Cell
          rowHeight={rowHeight}
          formatter={formatter}
          value={row[metric]?.[field]}
          percent={
            total?.[field] ? row[metric]?.[field] / total?.[field] : undefined
          }
        />
      );
    },
  };
};
